import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import { Button, ClassNameProps, Column, DistributiveOmit, Grid, If, Row, StyleProps } from "@hex-insights/core";
import {
	anyFieldChanged,
	FieldState,
	Form,
	FormState,
	SubmitButton,
	useFieldState,
	useInternalFields,
} from "@hex-insights/forms";
import {
	StudentDataConsentCreateMutation,
	StudentDataConsentDetailQuery,
	StudentDataConsentUpdateMutation,
} from "../../../Utilities";
import { StudentDataConsentFormat } from "../../Format";
import { StudentDataConsentFormState, StudentDataConsentFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import { useCreateFormControl, useCreateFormSubmission, useDetailFormControl, useDetailFormSubmission } from "./hooks";
import styles from "./student-form.module.css";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

const createInternalFields: (keyof StudentDataConsentFormValues.Create)[] = ["studentID"];

export type ControlledCreateProps = Omit<
	CreateProps<StudentDataConsentCreateMutation["createStudentDataConsent"]>,
	"formState" | "create"
> & {
	formStateArgs?: StudentDataConsentFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the StudentDataConsent model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<StudentDataConsentFormValues.Create>;
	create: CreateFunction<StudentDataConsentFormValues.Create, T>;
	onSuccess: (formState: FormState<StudentDataConsentFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<StudentDataConsentFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the StudentDataConsent model using the given form state.
 */
export function Create<T>({ formState, create, onSuccess, formNameSuffix, ...props }: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "studentDataConsent.create" + (formNameSuffix ? "." + formNameSuffix : "");

	useInternalFields(formState, createInternalFields);

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<QuestionFields formState={formState} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Submit
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

export type ControlledCreateWithPresetProps = Omit<
	CreateWithPresetProps<StudentDataConsentCreateMutation["createStudentDataConsent"]>,
	"formState" | "create"
> & {
	formStateArgs?: StudentDataConsentFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the StudentDataConsent model using an internally managed form state.
 */
export function ControlledCreateWithPreset({ formStateArgs, ...props }: ControlledCreateWithPresetProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <CreateWithPreset formState={formState} create={create} {...props} />;
}

export type CreateWithPresetProps<T> = {
	formState: FormState<StudentDataConsentFormValues.Create>;
	create: CreateFunction<StudentDataConsentFormValues.Create, T>;
	onSuccess: (formState: FormState<StudentDataConsentFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
	columnClassName?: string;
} & BaseFormProps<StudentDataConsentFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the StudentDataConsent model using the given form state.
 */
export function CreateWithPreset<T>({
	formState,
	create,
	onSuccess,
	formNameSuffix,
	columnClassName,
	...props
}: CreateWithPresetProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "studentDataConsent.create" + (formNameSuffix ? "." + formNameSuffix : "");

	useInternalFields(formState, createInternalFields);

	const { selectedPreset, onSelectPreset } = useQuestionsPreset({ formState, initiallyBlank: true });

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column verticalSpacing="1.5rem" className={columnClassName}>
				<QuestionsPresetField selectedPreset={selectedPreset} onSelectPreset={onSelectPreset} />

				<QuestionFields formState={formState} hidden={selectedPreset !== "custom"} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Save Preferences
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

export type ControlledDetailProps = Omit<
	DetailProps<StudentDataConsentUpdateMutation["updateStudentDataConsent"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<StudentDataConsentFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the StudentDataConsent model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({
		...formStateArgs,
		studentDataConsent: props.studentDataConsent,
	});

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	studentDataConsent: StudentDataConsentDetailQuery["studentDataConsent"];
	formState: FormState<StudentDataConsentFormValues.Detail>;
	update: UpdateFunction<StudentDataConsentFormValues.Detail, T>;
	onSuccess: (formState: FormState<StudentDataConsentFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<StudentDataConsentFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the StudentDataConsent model using the given form state.
 */
export function Detail<T>({
	formState,
	studentDataConsent,
	update,
	onSuccess,
	formNameSuffix,
	noChangesDialog = false,
	...props
}: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName = `studentDataConsent.detail.${studentDataConsent.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form
			name={formName}
			formState={formState}
			onSubmit={formState.onSubmitWrapper(onSubmit)}
			noChangesDialog={noChangesDialog}
			{...props}
		>
			<Form.Column>
				<DetailFields formState={formState} studentDataConsent={studentDataConsent} />

				<If condition={noChangesDialog && anyFieldChanged(formState)}>
					<Form.ActionBar>
						<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
							Save Changes
						</SubmitButton>
					</Form.ActionBar>
				</If>
			</Form.Column>
		</Form>
	);
}

const detailInternalFields: (keyof StudentDataConsentFormValues.Detail)[] = [
	"createdAt",
	"updatedAt",
	"authorID",
	"studentID",
];

type DetailFieldsProps = {
	studentDataConsent: StudentDataConsentDetailQuery["studentDataConsent"];
	formState: FormState<StudentDataConsentFormValues.Detail>;
};

function DetailFields({ formState }: DetailFieldsProps) {
	useInternalFields(formState, detailInternalFields);

	return <QuestionFields formState={formState} />;
}

type QuestionFormValues = Pick<
	StudentDataConsentFormValues.Base,
	| "allowPublishVisualMediaOfStudentGeneral"
	| "allowPublishVisualMediaOfStudentForPromotion"
	| "allowPublishStudentWorkGeneral"
	| "allowPublishVisualMediaOfGuardianForPromotion"
	| "allowShareInformationWithPearsonEdexcel"
	| "allowShareInformationWithIbOrganization"
	| "allowShareInformationWithOnlinePlatforms"
	| "allowBirthdayInCalendar"
	| "allowSendNewsletters"
	| "allowSendCalendarEvents"
>;

export type UseQuestionsPresetArgs = {
	formState: FormState<QuestionFormValues>;
	initiallyBlank?: boolean;
};

export function useQuestionsPreset({ formState, initiallyBlank }: UseQuestionsPresetArgs) {
	const [selectedPreset, setSelectedPreset] = React.useState<DataConsentPreset | null>(() => {
		if (initiallyBlank) {
			return null;
		}

		const booleanFields = [
			"allowPublishVisualMediaOfStudentGeneral",
			"allowPublishVisualMediaOfStudentForPromotion",
			"allowPublishVisualMediaOfGuardianForPromotion",
			"allowPublishStudentWorkGeneral",
			"allowShareInformationWithPearsonEdexcel",
			"allowShareInformationWithIbOrganization",
			"allowShareInformationWithOnlinePlatforms",
			"allowBirthdayInCalendar",
			"allowSendNewsletters",
			"allowSendCalendarEvents",
		];

		const all = formState.formValues[booleanFields[0]];
		for (let i = 0; i < booleanFields.length; i++) {
			const value = formState.formValues[booleanFields[i]];
			if (value !== all) {
				return "custom";
			}
		}

		return all ? "yes" : "custom";
	});

	const onSelectPreset = React.useCallback(
		(preset: DataConsentPreset) => {
			const value = true;

			setSelectedPreset(preset);
			formState.formSetFunctions.allowPublishVisualMediaOfStudentGeneral(value);
			formState.formSetFunctions.allowPublishVisualMediaOfStudentForPromotion(value);
			formState.formSetFunctions.allowPublishVisualMediaOfGuardianForPromotion(value);
			formState.formSetFunctions.allowPublishStudentWorkGeneral(value);
			formState.formSetFunctions.allowShareInformationWithPearsonEdexcel(value);
			formState.formSetFunctions.allowShareInformationWithIbOrganization(value);
			formState.formSetFunctions.allowShareInformationWithOnlinePlatforms(value);
			formState.formSetFunctions.allowBirthdayInCalendar(value);
			formState.formSetFunctions.allowSendNewsletters(value);
			formState.formSetFunctions.allowSendCalendarEvents(value);
		},
		[formState.formSetFunctions],
	);

	return { selectedPreset, onSelectPreset };
}

export type DataConsentPreset = "yes" | "custom";

export type QuestionsPresetFieldProps = {
	selectedPreset: DataConsentPreset | null;
	onSelectPreset: (preset: DataConsentPreset) => void;
} & Partial<ClassNameProps & StyleProps>;

export function QuestionsPresetField({ selectedPreset, onSelectPreset, className, style }: QuestionsPresetFieldProps) {
	return (
		<Column justify="spaced-start" verticalSpacing="1rem" className={className} style={style}>
			<span className={styles["bold-heading-text"]}>
				Please verify your express GDPR consents as they were set by you in the re-enrollment and admission forms (they
				are under Customize button).
			</span>

			<Grid tracks="auto-fill" minTrackWidth="15rem" gap="0.75rem">
				<Button
					variant={selectedPreset === "yes" ? "primary" : "tertiary"}
					onClick={() => onSelectPreset("yes")}
					className={styles["button"] + " button--primary-tertiary"}
					style={{ width: "100%" }}
				>
					Yes, I consent to all
				</Button>
				<Button
					variant={selectedPreset === "custom" ? "primary" : "tertiary"}
					onClick={() => onSelectPreset("custom")}
					className={styles["button"] + " button--primary-tertiary"}
					style={{ width: "100%" }}
				>
					No, Customize
				</Button>
			</Grid>
		</Column>
	);
}

export type QuestionFieldsProps = {
	formState: FormState<QuestionFormValues>;
	hidden?: boolean;
};

export function QuestionFields({ formState, hidden = false }: QuestionFieldsProps) {
	const allowPublishVisualMediaOfStudentGeneralFieldState = useFieldState<QuestionFormValues, boolean>({
		formState,
		name: "allowPublishVisualMediaOfStudentGeneral",
	});
	const allowPublishVisualMediaOfStudentForPromotionFieldState = useFieldState<QuestionFormValues, boolean>({
		formState,
		name: "allowPublishVisualMediaOfStudentForPromotion",
	});
	const allowPublishStudentWorkGeneralFieldState = useFieldState<QuestionFormValues, boolean>({
		formState,
		name: "allowPublishStudentWorkGeneral",
	});
	const allowPublishVisualMediaOfGuardianForPromotionFieldState = useFieldState<QuestionFormValues, boolean>({
		formState,
		name: "allowPublishVisualMediaOfGuardianForPromotion",
	});
	const allowShareInformationWithPearsonEdexcelFieldState = useFieldState<QuestionFormValues, boolean>({
		formState,
		name: "allowShareInformationWithPearsonEdexcel",
	});
	const allowShareInformationWithIbOrganizationFieldState = useFieldState<QuestionFormValues, boolean>({
		formState,
		name: "allowShareInformationWithIbOrganization",
	});
	const allowShareInformationWithOnlinePlatformsFieldState = useFieldState<QuestionFormValues, boolean>({
		formState,
		name: "allowShareInformationWithOnlinePlatforms",
	});
	const allowBirthdayInCalendarFieldState = useFieldState<QuestionFormValues, boolean>({
		formState,
		name: "allowBirthdayInCalendar",
	});
	const allowSendNewslettersFieldState = useFieldState<QuestionFormValues, boolean>({
		formState,
		name: "allowSendNewsletters",
	});
	const allowSendCalendarEventsFieldState = useFieldState<QuestionFormValues, boolean>({
		formState,
		name: "allowSendCalendarEvents",
	});

	if (hidden) {
		return null;
	}

	return (
		<Column justify="spaced-start" verticalSpacing="1rem">
			<span className={styles["bold-heading-text"]}>Please select if you allow Verita to:</span>

			<QuestionField
				fieldState={allowPublishVisualMediaOfStudentGeneralFieldState}
				label={StudentDataConsentFormat.fieldLabels.allowPublishVisualMediaOfStudentGeneral}
			/>

			<QuestionField
				fieldState={allowPublishVisualMediaOfStudentForPromotionFieldState}
				label={StudentDataConsentFormat.fieldLabels.allowPublishVisualMediaOfStudentForPromotion}
			/>

			<QuestionField
				fieldState={allowPublishStudentWorkGeneralFieldState}
				label={StudentDataConsentFormat.fieldLabels.allowPublishStudentWorkGeneral}
			/>

			<QuestionField
				fieldState={allowPublishVisualMediaOfGuardianForPromotionFieldState}
				label={StudentDataConsentFormat.fieldLabels.allowPublishVisualMediaOfGuardianForPromotion}
			/>

			<QuestionField
				fieldState={allowShareInformationWithPearsonEdexcelFieldState}
				label={StudentDataConsentFormat.fieldLabels.allowShareInformationWithPearsonEdexcel}
			/>

			<QuestionField
				fieldState={allowShareInformationWithIbOrganizationFieldState}
				label={StudentDataConsentFormat.fieldLabels.allowShareInformationWithIbOrganization}
			/>

			<QuestionField
				fieldState={allowShareInformationWithOnlinePlatformsFieldState}
				label={StudentDataConsentFormat.fieldLabels.allowShareInformationWithOnlinePlatforms}
			/>

			<QuestionField
				fieldState={allowBirthdayInCalendarFieldState}
				label={StudentDataConsentFormat.fieldLabels.allowBirthdayInCalendar}
			/>

			<QuestionField
				fieldState={allowSendNewslettersFieldState}
				label={StudentDataConsentFormat.fieldLabels.allowSendNewsletters}
			/>

			<QuestionField
				fieldState={allowSendCalendarEventsFieldState}
				label={StudentDataConsentFormat.fieldLabels.allowSendCalendarEvents}
			/>
		</Column>
	);
}

type QuestionFieldProps = {
	fieldState: FieldState<boolean>;
	label: string;
};

function QuestionField({ fieldState, label }: QuestionFieldProps) {
	return (
		<Column justify="spaced-start" verticalSpacing="1rem">
			<span className={styles["question-field__label"]}>{label}</span>
			<Row justify="spaced-start">
				<Button
					variant={fieldState.value ? "primary" : "tertiary"}
					onClick={() => fieldState.setValue(true)}
					className={styles["button"] + " button--primary-tertiary"}
				>
					Yes
				</Button>
				<Button
					variant={!fieldState.value ? "primary" : "tertiary"}
					onClick={() => fieldState.setValue(false)}
					className={styles["button"] + " button--primary-tertiary"}
				>
					No
				</Button>
			</Row>
		</Column>
	);
}
